import { ref } from "vue";
import type { Ref } from "vue";
import type {
  MaybeRef,
  SureRef,
  MaybeRefObjectMaybeProperty,
} from "@/typings/refs";
import getProperty from "@/functions/getProperty";
import useToggle from "@/composables/useToggle";
import { UseTimerOptions } from "@/typings/useTimer";
/**
 * @function useTimer - composable to use timers and intervals in a more vue friendly way
 * @param {Ref<number>|number}time - time in mili seconds for the timer
 * @param { MaybeRef<UseTimerOptions>} options - timer options
 * @returns timer flag, starter and stopper functions
 */
export default function useTimer(
  time: SureRef<number>,
  options?: MaybeRef<UseTimerOptions>
): { timer: Ref<boolean>; start: () => void; stop: () => void } {
  /** @const {Ref<number>} seconds - time in mili seconds that were provided */
  const seconds = ref(time);
  /** @const {Ref<number>} timed - if the time is actually positive */
  const timed = ref(seconds.value > 0);

  /** @const {Ref<boolean>} repeated - if the timer is interval */
  const repeated = getProperty(
    options as MaybeRefObjectMaybeProperty<boolean>,
    "repeat",
    false
  );
  /** @const {Ref<boolean>} immediate - if the timer start with the function trigger */
  const immediate = getProperty(
    options as MaybeRefObjectMaybeProperty<boolean>,
    "immediate",
    false
  );

  //flag that changes once the timeout is reached
  const { value: timer, toggle } = useToggle();

  const timerRef: Ref<number> = ref(0);
  function start() {
    if (timed.value) {
      if (repeated.value) {
        timerRef.value = setInterval(toggle, seconds.value);
      } else {
        timerRef.value = setTimeout(toggle, seconds.value);
      }
    }
  }

  function stop() {
    if (timed.value) {
      if (repeated.value) {
        clearInterval(timerRef.value);
      } else {
        clearTimeout(timerRef.value);
      }
    }
  }

  if (immediate.value == true) {
    start();
  }

  //return the timer flag, a trigger function and a stopper function
  return { timer, start, stop };
}
